/**
 * Collection load-more / infinite scroll.
 *
 * The section paginates products by 24 ({% paginate collection.products by 24 %}),
 * so only the first page is in the DOM on load. This script:
 *  1. Reveals the server-rendered cards.
 *  2. Watches the #collection-load-more-wrapper sentinel with an IntersectionObserver
 *     and fetches the next page (via the Section Rendering API) when it scrolls
 *     into view, appending the new cards to #product-grid.
 *  3. Hides the spinner wrapper when there is no next page, so it never spins forever.
 *
 * Re-inits when collection content is replaced (e.g. after facet filter).
 * Uses event delegation so grid buttons work after filter and on mobile/resize.
 */
(function () {
  var STORAGE_KEY = 'collection_grid_cols';
  var STORAGE_KEY_MOBILE = 'collection_grid_cols_mobile';
  var OBSERVER_MARGIN = '800px 0px';

  function getSectionEl(fromSectionId) {
    if (fromSectionId) {
      var el = document.getElementById('shopify-section-' + fromSectionId);
      if (el) return el;
    }
    var grid = document.getElementById('product-grid');
    return grid ? grid.closest('[id^="shopify-section-"]') : null;
  }

  function getSectionId(sectionEl) {
    return sectionEl ? sectionEl.id.replace('shopify-section-', '') : null;
  }

  function setGridClass(sectionEl, cols) {
    if (!sectionEl) return;
    sectionEl.classList.remove('collection--grid-3', 'collection--grid-4', 'collection--grid-5', 'collection--grid-6', 'active_cards');
    sectionEl.classList.add('collection--grid-' + cols);
    if (cols === 6) sectionEl.classList.add('active_cards');
    try {
      localStorage.setItem(STORAGE_KEY, String(cols));
      if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 999px)').matches) {
        localStorage.setItem(STORAGE_KEY_MOBILE, String(cols));
      }
    } catch (e) {}
  }

  function getWrapper(sectionEl) {
    return sectionEl ? sectionEl.querySelector('#collection-load-more-wrapper') : null;
  }

  function hasNextPage(sectionEl) {
    var wrapper = getWrapper(sectionEl);
    var nextUrl = wrapper ? (wrapper.getAttribute('data-next-url') || '').trim() : '';
    return nextUrl.length > 0;
  }

  function updateProgressForSection(sectionEl) {
    if (!sectionEl) return;
    var grid = sectionEl.querySelector('#product-grid');
    if (!grid) return;
    var productCards = Array.from(grid.querySelectorAll('.product-card'));
    var toolbar = sectionEl.querySelector('.collection__grid-toolbar');
    var totalProducts = toolbar ? parseInt(toolbar.getAttribute('data-total-products') || '0', 10) : 0;
    var total = totalProducts > 0 ? totalProducts : productCards.length;
    var visibleCount = productCards.filter(function (c) { return c.classList.contains('visible'); }).length;
    var displayCount = Math.min(visibleCount, total);
    var totalForProgress = total > 0 ? total : 1;
    var pct = Math.round((displayCount / totalForProgress) * 100);
    var loadMoreStatus = sectionEl.querySelector('#load-more-status');
    var progressBar = sectionEl.querySelector('#load-more-progress-bar');
    var loadMoreBtn = sectionEl.querySelector('#load-more-btn');
    var loadMoreWrapper = getWrapper(sectionEl);
    var moreToLoad = hasNextPage(sectionEl) || visibleCount < productCards.length;
    if (loadMoreStatus) loadMoreStatus.textContent = 'Viewing 1 - ' + displayCount + ' out of ' + total + ' products';
    if (progressBar) progressBar.style.width = pct + '%';
    if (progressBar) progressBar.setAttribute('aria-valuenow', displayCount);
    if (loadMoreBtn) loadMoreBtn.style.display = moreToLoad ? '' : 'none';
    /* Only show the spinner wrapper while there are more pages to fetch */
    if (loadMoreWrapper) loadMoreWrapper.style.display = moreToLoad ? 'flex' : 'none';
  }

  function revealCards(grid) {
    Array.from(grid.querySelectorAll('.product-card')).forEach(function (card) {
      card.classList.add('visible');
    });
  }

  function buildSectionUrl(nextUrl, sectionId) {
    try {
      var url = new URL(nextUrl, window.location.origin);
      url.searchParams.set('section_id', sectionId);
      return url.toString();
    } catch (e) {
      return nextUrl + (nextUrl.indexOf('?') >= 0 ? '&' : '?') + 'section_id=' + sectionId;
    }
  }

  function loadNextPage(sectionEl) {
    if (!sectionEl || sectionEl.dataset.loadMoreLoading === 'true') return;
    var wrapper = getWrapper(sectionEl);
    var grid = sectionEl.querySelector('#product-grid');
    if (!wrapper || !grid) return;
    var nextUrl = (wrapper.getAttribute('data-next-url') || '').trim();
    if (!nextUrl) {
      updateProgressForSection(sectionEl);
      return;
    }
    var sectionId = getSectionId(sectionEl);
    if (!sectionId) return;

    sectionEl.dataset.loadMoreLoading = 'true';

    fetch(buildSectionUrl(nextUrl, sectionId))
      .then(function (response) {
        if (!response.ok) throw new Error('Load more failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newGrid = doc.querySelector('#product-grid');
        var newWrapper = doc.querySelector('#collection-load-more-wrapper');

        if (newGrid) {
          Array.from(newGrid.children).forEach(function (child) {
            grid.appendChild(child);
          });
          revealCards(grid);
        }

        var newNextUrl = newWrapper ? (newWrapper.getAttribute('data-next-url') || '').trim() : '';
        wrapper.setAttribute('data-next-url', newNextUrl);

        updateProgressForSection(sectionEl);

        /* If the sentinel is still in view (short pages), keep loading */
        if (newNextUrl && isInViewport(wrapper)) {
          requestAnimationFrame(function () { loadNextPage(sectionEl); });
        }
      })
      .catch(function () {
        /* Fail quietly; the observer will retry on the next scroll into view */
      })
      .finally(function () {
        delete sectionEl.dataset.loadMoreLoading;
      });
  }

  function isInViewport(el) {
    var rect = el.getBoundingClientRect();
    return rect.top < (window.innerHeight || document.documentElement.clientHeight) + 800 && rect.bottom > -800;
  }

  function setupObserver(sectionEl) {
    var wrapper = getWrapper(sectionEl);
    if (!wrapper) return;

    /* Disconnect a previous observer (e.g. after facet re-render) */
    if (sectionEl._loadMoreObserver) {
      sectionEl._loadMoreObserver.disconnect();
      sectionEl._loadMoreObserver = null;
    }

    if (!hasNextPage(sectionEl)) {
      wrapper.style.display = 'none';
      return;
    }

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) loadNextPage(sectionEl);
        });
      }, { rootMargin: OBSERVER_MARGIN });
      observer.observe(wrapper);
      sectionEl._loadMoreObserver = observer;
    } else {
      /* Fallback: load on scroll near bottom */
      var onScroll = function () {
        if (hasNextPage(sectionEl) && isInViewport(wrapper)) loadNextPage(sectionEl);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
    }
  }

  function init(sectionId) {
    var sectionEl = getSectionEl(sectionId);
    if (!sectionEl) return;

    var grid = sectionEl.querySelector('#product-grid');
    if (!grid) return;

    var toolbar = sectionEl.querySelector('.collection__grid-toolbar');
    var isMobile = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 999px)').matches;
    var savedCols = 4;
    try {
      savedCols = parseInt(isMobile ? (localStorage.getItem(STORAGE_KEY_MOBILE) || '4') : (localStorage.getItem(STORAGE_KEY) || '4'), 10);
    } catch (e) {}
    if (!isMobile && savedCols === 3) savedCols = 4;
    if (savedCols >= 3 && savedCols <= 6) {
      setGridClass(sectionEl, savedCols);
      var btns = sectionEl.querySelectorAll('.collection__grid-view-btns button');
      if (btns.length) {
        btns.forEach(function (btn) {
          var c = parseInt(btn.getAttribute('data-cols'), 10);
          if (c === savedCols) {
            btn.classList.add('is-active');
            btn.setAttribute('aria-pressed', 'true');
          } else {
            btn.classList.remove('is-active');
            btn.setAttribute('aria-pressed', 'false');
          }
        });
      }
    }

    revealCards(grid);
    grid.setAttribute('data-load-more-initialized', 'true');
    updateProgressForSection(sectionEl);
    setupObserver(sectionEl);
  }

  function onGridButtonClick(btn) {
    var sectionEl = btn.closest('[id^="shopify-section-"]');
    if (!sectionEl) return;
    var cols = parseInt(btn.getAttribute('data-cols'), 10);
    if (isNaN(cols) || cols < 3 || cols > 6) return;
    setGridClass(sectionEl, cols);
    /* Update all grid toolbars in section (desktop + mobile) so active state stays in sync */
    sectionEl.querySelectorAll('.collection__grid-toolbar').forEach(function (toolbar) {
      toolbar.querySelectorAll('.collection__grid-view-btns button').forEach(function (b) {
        var c = parseInt(b.getAttribute('data-cols'), 10);
        b.classList.toggle('is-active', c === cols);
        b.setAttribute('aria-pressed', c === cols ? 'true' : 'false');
      });
    });
    updateProgressForSection(sectionEl);
  }

  document.addEventListener('click', function (e) {
    var gridBtn = e.target.closest('.collection__grid-view-btns button[data-cols]');
    if (gridBtn) {
      e.preventDefault();
      onGridButtonClick(gridBtn);
      return;
    }
    var loadMoreBtn = e.target.closest('#load-more-btn');
    if (loadMoreBtn) {
      e.preventDefault();
      var sectionEl = loadMoreBtn.closest('[id^="shopify-section-"]');
      loadNextPage(sectionEl);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('facet:content-replaced', function (e) {
    var sectionId = e.detail && e.detail.sectionId;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        init(sectionId);
      });
    });
  });
})();